/**
 * Types for the reporting dashboard builder.
 *
 * A dashboard is persisted as a single `Report` (`reportType: 'dashboard'`) whose
 * `searchConfig` holds a `DashboardSearchConfig` — the grid settings plus every
 * widget's config inline. No `Dashboard`/`DashboardReport` join rows; the whole
 * dashboard saves and loads as one record (atomic) and is tracked via the existing
 * report-tracking structure keyed on that report's id.
 *
 * Two widget types:
 *   - counter → a single KPI value (one measure, no axes).
 *   - chart   → field wells (QuickSight-style): X axis + Bars measures + Lines
 *               measures + optional Group/color dimension. Bar-vs-line is decided
 *               by which well a measure sits in; stacked/clustered + a secondary
 *               line axis are toggles. Collapses bar/stacked/line into one type.
 */

import type {
  AggregationType,
  ColumnAggregationMap,
  DateGroupingInterval,
  DateGroupingMap,
} from '../aggregation-types.ts';
import type { ColumnFormatConfig, NumberFormatConfig } from '../column-format-options.ts';
import type { FilterLayout, FilterOperator } from '../filter-operators.ts';
import type { SortEntry } from '../sort-helpers.ts';

/** X-axis label tilt. */
export type LabelAngle = 0 | 45 | 90;

/** Legend placement. */
export type LegendPosition = 'top' | 'right' | 'bottom' | 'left';

export interface AxisFormat {
  title?: string;
  /** X-axis only — tick label tilt (default 45 when dense). */
  labelAngle?: LabelAngle;
  /** Value format (reuses the report builder's column formats). */
  valueFormat?: ColumnFormatConfig;
}

export interface ValueAxisFormat {
  title?: string;
  valueFormat?: NumberFormatConfig;
}

/** Display/formatting layer for a chart widget (separate from the data wells). */
export interface ChartFormat {
  /** Manual canvas height (px); overrides the size preset when set. */
  height?: number;
  xAxis?: AxisFormat;
  yLeft?: ValueAxisFormat;
  yRight?: ValueAxisFormat;
  legend?: { show?: boolean; position?: LegendPosition };
  dataLabels?: { show?: boolean; valueFormat?: NumberFormatConfig };
  /** Per-series color override, keyed by series label (TIO palette hex). */
  colors?: Record<string, string>;
}

/** Report type discriminator for dashboards (vs table_builder / chart_builder). */
export const DASHBOARD_REPORT_TYPE = 'dashboard';

export type WidgetType = 'counter' | 'chart' | 'pie' | 'table';

/** Max pie slices before the remainder is rolled into an "Other" slice. */
export const PIE_SLICE_LIMIT = 20;

/** Character cap for a widget's optional free-text description. */
export const DESCRIPTION_MAX_LENGTH = 140;

export type WidgetSize = 'small' | 'medium' | 'large';

/** Bars rendered stacked (default) or side-by-side. */
export type BarMode = 'stacked' | 'clustered';

/** Bars run vertical (default) or horizontal — horizontal is bars-only (no lines). */
export type ChartOrientation = 'vertical' | 'horizontal';

/** Lines plotted on their own right-hand axis (default) or sharing the left axis. */
export type LinesAxis = 'secondary' | 'left';

/** Where a field chosen from the field rail lands; `column` is the table widget's only role. */
export type WidgetRole = 'x' | 'group' | 'bar' | 'line' | 'measure' | 'slice' | 'value' | 'column';

/** A grouping dimension; `dateInterval` is set for date fields (date_histogram). */
export interface WidgetDimension {
  fieldKey: string;
  dateInterval?: DateGroupingInterval;
}

/** A measure in the counter widget. `fieldKey: null` + `count` = count of documents. */
export interface WidgetMeasure {
  fieldKey: string | null;
  aggregation: AggregationType;
}

/** A measure in a Bars/Lines well. `fieldKey: null` + `count` = count of documents. */
export interface MeasureSpec {
  /** Stable id for keying/removal within a well. */
  id: string;
  fieldKey: string | null;
  aggregation: AggregationType;
  /** Optional display label; derived from field + aggregation when absent. */
  label?: string;
}

/**
 * Free-form canvas placement (px), stored inline on each widget. Widgets are
 * absolutely positioned; overlaps are allowed (the user arranges as needed).
 * Position is free; width/height snap to RESIZE_SNAP steps when resized.
 */
export interface WidgetLayout {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Filter state in the report builder's shape so the same UI (ActiveFiltersZone /
 * FilterRow) and clause assembler (buildConfiguredFilterClauses) apply.
 *
 * `filterValues` is keyed by `fieldKey` or `fieldKey__operator`; `filterOperators`
 * records the chosen operator per base field. Build filters additionally carry a
 * `filterLayout` for top-level AND vs OR-group placement; user filters are flat.
 */
export interface BuildFilterConfig {
  selectedFilters: string[];
  filterValues: Record<string, unknown>;
  filterOperators: Record<string, FilterOperator>;
  filterLayout: FilterLayout;
}

export interface UserFilterConfig {
  selectedFilters: string[];
  filterValues: Record<string, unknown>;
  filterOperators: Record<string, FilterOperator>;
}

export function emptyBuildFilterConfig(): BuildFilterConfig {
  return { selectedFilters: [], filterValues: {}, filterOperators: {}, filterLayout: [] };
}

export function emptyUserFilterConfig(): UserFilterConfig {
  return { selectedFilters: [], filterValues: {}, filterOperators: {} };
}

/** True when a build/user filter config carries no active filters. */
export function isFilterConfigEmpty(
  config: BuildFilterConfig | UserFilterConfig | undefined
): boolean {
  return !config?.selectedFilters?.length;
}

/**
 * Coerce a persisted value into a valid filter config. Dashboards saved before
 * the filter-model migration carry the old array shape (or partial objects);
 * anything malformed degrades to an empty config rather than crashing at read.
 */
export function normalizeBuildFilterConfig(raw: unknown): BuildFilterConfig {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return emptyBuildFilterConfig();
  const cfg = raw as Partial<BuildFilterConfig>;
  return {
    selectedFilters: Array.isArray(cfg.selectedFilters) ? cfg.selectedFilters : [],
    filterValues: cfg.filterValues ?? {},
    filterOperators: cfg.filterOperators ?? {},
    filterLayout: Array.isArray(cfg.filterLayout) ? cfg.filterLayout : [],
  };
}

export function normalizeUserFilterConfig(raw: unknown): UserFilterConfig {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return emptyUserFilterConfig();
  const cfg = raw as Partial<UserFilterConfig>;
  return {
    selectedFilters: Array.isArray(cfg.selectedFilters) ? cfg.selectedFilters : [],
    filterValues: cfg.filterValues ?? {},
    filterOperators: cfg.filterOperators ?? {},
  };
}

/**
 * A table widget's tabular definition, built in the widget editor or copied
 * inline from a saved report (the dashboard stays self-contained; later edits
 * to the source report do not affect it). Rendered via the report builder's
 * PreviewTable. The widget's own `dataset` / `showIndividualApplicationsOnly` /
 * `buildFilter` carry the rest of the state.
 */
export interface TableWidgetConfig {
  /** Provenance only — shown in the editor, never re-fetched. */
  sourceReportId?: string;
  sourceReportName?: string;
  /** Ordered column keys (resolved against the dataset's field registry). */
  selectedColumns: string[];
  sorts: SortEntry[];
  columnLabels?: Record<string, string>;
  columnAltTexts?: Record<string, string>;
  columnFormats?: Record<string, ColumnFormatConfig>;
  groupBy?: {
    selectedFields: string[];
    columnAggregations: ColumnAggregationMap;
    dateGroupingIntervals?: DateGroupingMap;
  };
  /** Rows per page (default 10). */
  pageSize?: number;
}

export interface WidgetConfig {
  /** Stable client id used for keying, selection, and reorder. */
  id: string;
  widgetType: WidgetType;
  /** Title shown on the tile. */
  label?: string;
  /**
   * Optional longer free-text description (capped at DESCRIPTION_MAX_LENGTH).
   * Surfaced under the counter value, as a hover title on the tile header, and
   * as a subtitle in the expanded-widget overlay.
   */
  description?: string;
  /** Dataset slug from allDatasets() (dataset-utils) — each widget chooses its own. */
  dataset: string;
  /**
   * Index sub-selection — when the dataset's model is TasProgramInstance, true
   * resolves to the TasApplication index (mirrors the report builder toggle).
   */
  showIndividualApplicationsOnly?: boolean;

  // ── counter ────────────────────────────────────────────────────────────────
  measure?: WidgetMeasure;

  // ── chart field wells ────────────────────────────────────────────────────────
  /** X axis dimension (categories / time). */
  xDimension?: WidgetDimension;
  /** Group/color dimension — splits bars into series (bars only). */
  groupDimension?: { fieldKey: string };
  /** Measures drawn as bars. */
  bars?: MeasureSpec[];
  /** Measures drawn as lines. */
  lines?: MeasureSpec[];
  /** Bars stacked (default) or clustered. */
  barMode?: BarMode;
  /** Lines on a secondary right axis (default) or the left axis. */
  linesAxis?: LinesAxis;
  /** Vertical (default) or horizontal bars (bars only). */
  orientation?: ChartOrientation;

  // ── pie ──────────────────────────────────────────────────────────────────────
  /** Pie slice dimension. */
  sliceDimension?: WidgetDimension;
  /** Pie slice value. */
  valueMeasure?: WidgetMeasure;
  /** Render the pie as a donut (ring). */
  donut?: boolean;

  // ── table ────────────────────────────────────────────────────────────────────
  /** Inline tabular definition (imported snapshot of a saved report). */
  table?: TableWidgetConfig;

  /** Display/formatting layer. */
  format?: ChartFormat;

  // ── shared ───────────────────────────────────────────────────────────────────
  /** Per-widget build filters, scoped to this widget's own dataset fields. */
  buildFilter?: BuildFilterConfig;
  limit?: number;
  layout: WidgetLayout;
}

export interface DashboardGridConfig {
  columns: number;
  gap: 'sm' | 'md' | 'lg';
}

/**
 * The shape stored in `Report.searchConfig` for a dashboard. Carries dashboard-level
 * company scope (company-first, like the report builder) and build/user filters.
 */
export interface DashboardSearchConfig {
  kind: 'dashboard';
  grid: DashboardGridConfig;
  widgets: WidgetConfig[];
  /** Dashboard-level company scope (chosen first); drives field availability + query scope. */
  companyId?: string;
  companyName?: string;
  /** Dashboard-level filters (single-dataset only). Build = baked, user = viewer-adjustable. */
  buildFilters?: BuildFilterConfig;
  userFilters?: UserFilterConfig;
}

/** Default 12-column grid. */
export const DEFAULT_GRID: DashboardGridConfig = { columns: 12, gap: 'md' };

/** Move/resize snap step (px) — the dotted grid shown during gestures uses this pitch. */
export const RESIZE_SNAP = 20;

/** Minimum widget dimensions (px). */
export const MIN_WIDGET_WIDTH = 160;
export const MIN_WIDGET_HEIGHT = 120;

/** Quick-set dimensions for the editor's size preset buttons. */
export const SIZE_PRESETS: Record<WidgetSize, { w: number; h: number }> = {
  small: { w: 280, h: 200 },
  medium: { w: 560, h: 320 },
  large: { w: 880, h: 400 },
};

/** Snap a dimension to the resize grid, respecting a minimum. */
export function snapDimension(value: number, min: number): number {
  return Math.max(min, Math.round(value / RESIZE_SNAP) * RESIZE_SNAP);
}

interface LegacyWidgetLayout {
  position?: number;
  size?: WidgetSize;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
}

/**
 * Ensure every widget carries a free-form {x,y,w,h} layout. Dashboards saved
 * before free-form positioning stored {position, size}; those are converted to
 * a vertical cascade using the size presets.
 */
export function normalizeWidgetLayouts(widgets: WidgetConfig[]): WidgetConfig[] {
  let nextY = 24;
  return widgets.map((widget) => {
    const layout = widget.layout as unknown as LegacyWidgetLayout;
    if (
      typeof layout?.x === 'number' &&
      typeof layout?.y === 'number' &&
      typeof layout?.w === 'number' &&
      typeof layout?.h === 'number'
    ) {
      return widget;
    }
    const preset = SIZE_PRESETS[layout?.size ?? 'medium'];
    const normalized = { ...widget, layout: { x: 24, y: nextY, ...preset } };
    nextY += preset.h + 16;
    return normalized;
  });
}

const WIDGET_TYPE_LABELS: Record<WidgetType, string> = {
  counter: 'Counter',
  chart: 'Chart',
  pie: 'Pie',
  table: 'Table',
};

export function widgetTypeLabel(type: WidgetType): string {
  return WIDGET_TYPE_LABELS[type];
}

/** Generate a stable client id (for widgets and measure specs). */
function newWidgetId(): string {
  const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
  return `w-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

/** A count-of-documents measure spec (the default for a new bar). */
export function countMeasureSpec(): MeasureSpec {
  return { id: newWidgetId(), fieldKey: null, aggregation: 'count' };
}

/** Field keys don't carry across datasets: field-bound parts reset, and a table keeps only its page size. */
export function widgetForDataset(widget: WidgetConfig, dataset: string): WidgetConfig {
  const pageSize = widget.table?.pageSize;
  const reset: Partial<WidgetConfig> =
    widget.widgetType === 'counter'
      ? { measure: { fieldKey: null, aggregation: 'count' } }
      : widget.widgetType === 'pie'
        ? { sliceDimension: undefined, valueMeasure: { fieldKey: null, aggregation: 'count' } }
        : widget.widgetType === 'table'
          ? { table: { selectedColumns: [], sorts: [], ...(pageSize ? { pageSize } : {}) } }
          : {
              xDimension: undefined,
              groupDimension: undefined,
              bars: [countMeasureSpec()],
              lines: [],
            };
  return { ...widget, dataset, ...reset };
}

/** Create a blank widget of the given type, cascaded by creation index. */
export function defaultWidget(type: WidgetType, dataset: string, index: number): WidgetConfig {
  const preset = SIZE_PRESETS[type === 'counter' ? 'small' : 'medium'];
  const offset = 24 + (index % 8) * 32;
  const base = {
    id: newWidgetId(),
    widgetType: type,
    label: widgetTypeLabel(type),
    dataset,
    showIndividualApplicationsOnly: true,
    layout: { x: offset, y: offset, ...preset },
  };
  if (type === 'counter') {
    return { ...base, measure: { fieldKey: null, aggregation: 'count' } };
  }
  if (type === 'pie') {
    return {
      ...base,
      valueMeasure: { fieldKey: null, aggregation: 'count' },
      donut: false,
    };
  }
  if (type === 'table') {
    return {
      ...base,
      layout: { ...base.layout, ...SIZE_PRESETS.large },
      table: { selectedColumns: [], sorts: [] },
    };
  }
  return {
    ...base,
    bars: [countMeasureSpec()],
    lines: [],
    barMode: 'stacked',
    linesAxis: 'secondary',
  };
}

/** True when this report record is a dashboard. */
export function isDashboardConfig(config: unknown): config is DashboardSearchConfig {
  return (
    !!config &&
    typeof config === 'object' &&
    (config as DashboardSearchConfig).kind === 'dashboard' &&
    Array.isArray((config as DashboardSearchConfig).widgets)
  );
}
